import { css } from '@emotion/react'
import { isValidElement, type ReactNode } from 'react'
import {
  Button,
  type ButtonProps,
  FlexContainer,
  type FlexContainerProps,
  Headline,
  mediaQueryHookQueries,
  Spacer,
  useMediaQuery,
} from '../../index'
import CenteredLayout, { type CenteredLayoutProps } from './CenteredLayout'

export interface CenteredPageTemplateProps {
  button?: ButtonProps
  children: ReactNode
  contentAlign?: FlexContainerProps['justify']
  footer?: ReactNode
  icon?: ReactNode
  size: 'small' | 'medium' | 'large'
  textAlign?: CenteredLayoutProps['textAlign']
  title?: string
  titleAlign?: 'center' | 'start' | 'end'
}

const FullWidthCenteredFlexContainer = (props: FlexContainerProps) => (
  <FlexContainer
    css={css`
      width: 100%;
    `}
    justify="center"
    {...props}
  />
)

/**
 * Simple layout with optional pre-defined content placeholders - icon, title, buttons.
 * @param buttons - Accepts a single entity or an array. Buttons of a nested array will be rendered in a row.
 * Elements of a flat array will be rendered in a column.
 * Two types of elements are accepted - object with CustomButtonProps, or a React element.
 */
const CenteredPageTemplate = ({
  size,
  title,
  icon,
  button,
  titleAlign = 'center',
  children,
  contentAlign = 'center',
  textAlign = 'center',
  footer,
}: CenteredPageTemplateProps) => {
  const isXsOnly = useMediaQuery(mediaQueryHookQueries.xsOnly)

  const getButtons = () => (
    <FullWidthCenteredFlexContainer>
      {isValidElement(button) ? (
        button
      ) : (
        <Button {...button} fullWidth={isXsOnly} />
      )}
    </FullWidthCenteredFlexContainer>
  )

  return (
    <CenteredLayout
      contentAlign={contentAlign}
      size={size}
      textAlign={textAlign}
    >
      <FlexContainer
        css={css`
          ${
            isXsOnly &&
            css`
              align-items: center;
              flex-grow: 1;
            `
          }
        `}
        direction="column"
        justify="center"
      >
        {icon && (
          <FlexContainer justify="center">
            <Spacer
              css={css`
                text-align: center;
              `}
              type="mb-4"
            >
              {icon}
            </Spacer>
          </FlexContainer>
        )}
        {title && (
          <Headline
            css={css`
              text-align: ${titleAlign};
              width: 100%;
            `}
            fontWeight={700}
            type="h2"
          >
            {title}
          </Headline>
        )}
        {children}
      </FlexContainer>
      {button && (
        <Spacer
          css={css`
            width: 100%;
          `}
          type="my-5"
        >
          <FlexContainer>{getButtons()}</FlexContainer>
        </Spacer>
      )}
      {footer && <Spacer type="mb-5">{footer}</Spacer>}
    </CenteredLayout>
  )
}

export default CenteredPageTemplate
